/**
 * The History tab's lists, derived once so the tab and the tab-bar count agree.
 *
 * Sorting a booking into "past tenant" vs "application" needs deposit evidence
 * (rows in `payments`), which the booking row cannot carry — so this wraps
 * `useTenantJourneys`, which already loads exactly those rows in one batch. The
 * Room page (for the tab count) and the History tab both call it; both read the
 * same React Query cache entry, though opening the tab may refetch stale rows.
 *
 * Without that evidence the Past / Applications split is unknowable, so those
 * two lists are withheld — while loading (`resolving`) and when the read failed
 * (`failed`). Guessing from weaker signs of a tenancy would either list an
 * unpaid applicant as a past tenant or hide a real one, and an empty list after
 * a failed read would pass for "nothing here".
 *
 * @see classifyRoomBooking — the phase rules, incl. what makes a real tenancy.
 */
import { useMemo } from "react";
import {
  classifyRoomBooking,
  roomOccupancyPhase,
  type BookingRow,
} from "@/hooks/v2/useProperties";
import { useTenantJourneys } from "@/hooks/v2/useTenantJourneys";
import type { TenantJourney } from "@/lib/tenantJourney";
import { hasRecordedMoveIn } from "@/lib/tenantJourneySignals";

export interface RoomHistoryLists {
  /** Committed tenancies that haven't started yet. */
  upcoming: BookingRow[];
  /** Tenancies that happened and concluded — moved in / deposit paid, then left. */
  past: BookingRow[];
  /** Bookings that never became a tenancy but are still on file. */
  applications: BookingRow[];
}

/**
 * Pure: split a room's bookings into the three History sections. The current
 * tenant and archived never-tenants belong to no section. `realTenancy` is keyed
 * by booking id; a missing id reads as "never a tenancy".
 */
export function partitionRoomHistory(
  bookings: readonly BookingRow[],
  realTenancy: ReadonlyMap<number, boolean>,
  now: Date = new Date()
): RoomHistoryLists {
  const lists: RoomHistoryLists = { upcoming: [], past: [], applications: [] };
  for (const b of bookings) {
    switch (
      classifyRoomBooking(b, now, realTenancy.get(Number(b.id)) === true)
    ) {
      case "upcoming":
        lists.upcoming.push(b);
        break;
      case "past":
        lists.past.push(b);
        break;
      case "application":
        lists.applications.push(b);
        break;
      // "current" → the Current tenant tab; "archived" → listed nowhere.
    }
  }
  return lists;
}

export interface RoomHistory extends RoomHistoryLists {
  /** Derived journey stage per booking id, for the row badges. */
  journeys: Map<number, TenantJourney>;
  /** The journey evidence is still loading (row badges show a placeholder). */
  journeysLoading: boolean;
  /**
   * Deposit evidence is still loading and some ended booking needs it, so Past and
   * Applications are withheld. Render a placeholder, never the unverified rows.
   */
  resolving: boolean;
  /**
   * The deposit rows could not be read and some ended booking needs them: Past and
   * Applications are withheld. Say so — an empty list would pass for real data.
   */
  failed: boolean;
}

export function useRoomHistory(
  bookings: readonly BookingRow[] | null | undefined
): RoomHistory {
  const {
    journeys,
    realTenancy,
    depositEvidenceFailed,
    depositEvidenceLoading,
    isLoading,
  } = useTenantJourneys(bookings);

  const lists = useMemo(
    () => partitionRoomHistory(bookings ?? [], realTenancy),
    [bookings, realTenancy]
  );

  // Only an ended booking with no recorded move-in is sorted by the deposit
  // rows; a recorded move-in already makes it a real tenancy.
  const needsDepositEvidence = (bookings ?? []).some(
    (b) =>
      roomOccupancyPhase(b, new Date()) === "ended" && !hasRecordedMoveIn(b)
  );
  const resolving = depositEvidenceLoading && needsDepositEvidence;
  const failed =
    !depositEvidenceLoading && depositEvidenceFailed && needsDepositEvidence;
  const withheld = resolving || failed;

  return {
    upcoming: lists.upcoming,
    past: withheld ? [] : lists.past,
    applications: withheld ? [] : lists.applications,
    journeys,
    journeysLoading: isLoading,
    resolving,
    failed,
  };
}
